import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { UserPlus, Trash2, Users2, X } from "lucide-react";

// Pannello riutilizzabile: gestione elenco utenti Presenze per un contesto (group / operational_site / blue_box).
// flushRef (opzionale): Map condivisa dal contenitore; il "Salva"/chiusura del contenitore salva le righe non ancora inviate.
export function AttendanceParticipants({ appaltoId, service, contextType, contextId, title = "Utenti Presenze", flushRef }) {
  const [items, setItems] = useState([]);
  const [orig, setOrig] = useState({});
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([{ nome: "", cognome: "", telefono: "" }]);
  const [addN, setAddN] = useState(10);
  const [from, setFrom] = useState("");
  const PASTE_COLS = ["nome", "cognome", "telefono"];

  const base = `appalto_id=${appaltoId}&service=${service}&context_type=${contextType}${contextId ? `&context_id=${contextId}` : ""}`;
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/attendance/participants?${base}`);
      setItems(r.data || []);
      setOrig(Object.fromEntries((r.data || []).map((x) => [x.id, x])));
    } catch (e) { setItems([]); toast.error(formatApiError(e.response?.data?.detail) || "Impossibile caricare gli Utenti Presenze"); }
    finally { setLoading(false); }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  const setRow = (i, k, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const addRow = () => setRows((rs) => [...rs, { nome: "", cognome: "", telefono: "" }]);
  const addRows = (n) => {
    const count = Math.max(1, Math.min(500, Math.floor(Number(n) || 0)));
    setRows((rs) => [...rs, ...Array.from({ length: count }, () => ({ nome: "", cognome: "", telefono: "" }))]);
  };
  const delRow = (i) => setRows((rs) => rs.filter((_, j) => j !== i));

  // Incolla tabellare da Excel/Sheets (stessa logica di MultiUserImport): distribuisce
  // celle su nome/cognome/telefono e crea automaticamente le righe necessarie.
  const handlePaste = (e, rowIdx, pasteIdx) => {
    const text = e.clipboardData?.getData("text");
    if (!text || !/[\t\n\r]/.test(text)) return; // incolla normale (singola cella) invariato
    e.preventDefault();
    let lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
    while (lines.length > 1 && lines[lines.length - 1] === "") lines.pop();
    const parsed = lines.map((l) => l.split("\t"));
    setRows((prev) => {
      const next = prev.map((row) => ({ ...row }));
      const needed = rowIdx + parsed.length;
      while (next.length < needed) next.push({ nome: "", cognome: "", telefono: "" });
      parsed.forEach((cells, ri) => {
        cells.forEach((val, ci) => {
          const key = PASTE_COLS[pasteIdx + ci];
          if (!key) return;
          next[rowIdx + ri][key] = (val ?? "").trim();
        });
      });
      return next;
    });
  };

  const pendingNames = () => rows.filter((r) => r.nome.trim() && r.cognome.trim())
    .map((r) => ({ nome: r.nome, cognome: r.cognome, telefono: r.telefono || "" }));
  const halfRows = () => rows.filter((r) => (r.nome.trim() ? 1 : 0) + (r.cognome.trim() ? 1 : 0) === 1).length;
  const postRows = async (names) => {
    const r = await api.post("/admin/attendance/participants/bulk", {
      appalto_id: appaltoId, service, context_type: contextType, context_id: contextId || null,
      names, valid_from: from || null });
    setRows([{ nome: "", cognome: "", telefono: "" }]);
    return r.data?.created ?? names.length;
  };
  const saveRows = async () => {
    const names = pendingNames();
    if (!names.length) { toast.error("Inserisci almeno un nominativo (nome e cognome)"); return; }
    try { const n = await postRows(names); toast.success(`${n} nominativi aggiunti`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const putItem = (it) => api.put(`/admin/attendance/participants/${it.id}`, {
    appalto_id: appaltoId, service, context_type: contextType, context_id: contextId || null,
    nome: it.nome, cognome: it.cognome, telefono: it.telefono || null,
    valid_from: it.valid_from || null, valid_to: it.valid_to || null });
  const saveEdit = async (it) => {
    try { await putItem(it); toast.success("Aggiornato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const isDirty = (it) => { const o = orig[it.id]; return !!o && ["nome", "cognome", "telefono", "valid_from"].some((k) => (o[k] || "") !== (it[k] || "")); };

  // Salvataggio automatico delle modifiche non inviate (usato dal "Salva" del contenitore). Lancia errore con messaggio.
  const flushLatest = useRef(null);
  flushLatest.current = async () => {
    if (halfRows()) throw new Error(`${title}: completa nome e cognome di ogni riga oppure svuotala.`);
    const names = pendingNames();
    const dirty = items.filter(isDirty);
    if (!names.length && !dirty.length) return 0;
    try {
      let n = 0;
      if (names.length) n += await postRows(names);
      for (const it of dirty) { await putItem(it); n += 1; }
      load();
      return n;
    } catch (e) { throw new Error(`${title}: ${formatApiError(e.response?.data?.detail) || "salvataggio non riuscito"}`); }
  };
  useEffect(() => {
    if (!flushRef) return undefined;
    const key = `${service}|${contextType}|${contextId || ""}|${appaltoId}`;
    flushRef.current.set(key, () => flushLatest.current());
    return () => { flushRef.current.delete(key); };
  }, [flushRef, service, contextType, contextId, appaltoId]);
  const endOne = async (it) => {
    try { await api.post(`/admin/attendance/participants/${it.id}/end`, {}); toast.success("Rimosso dal contesto"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setItem = (i, k, v) => setItems((its) => its.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 mt-3" data-testid="attendance-panel">
      <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-2"><Users2 className="h-4 w-4" /> {title}</p>

      {loading ? <p className="text-xs text-slate-400">Caricamento…</p> : items.length === 0 ? (
        <p className="text-xs text-slate-400 mb-2" data-testid="attendance-empty">Nessun utente inserito.</p>
      ) : (
        <div className="space-y-1.5 mb-3">
          {items.map((it, i) => (
            <div key={it.id} className="flex items-center gap-2" data-testid={`attendance-item-${it.id}`}>
              <Input value={it.nome} onChange={(e) => setItem(i, "nome", e.target.value)} className="h-8 text-sm flex-1 min-w-0" placeholder="Nome" />
              <Input value={it.cognome} onChange={(e) => setItem(i, "cognome", e.target.value)} className="h-8 text-sm flex-1 min-w-0" placeholder="Cognome" />
              <Input value={it.telefono || ""} onChange={(e) => setItem(i, "telefono", e.target.value)} className="h-8 text-sm flex-1 min-w-0" placeholder="Telefono" data-testid={`attendance-tel-${it.id}`} />
              <Input type="date" value={it.valid_from || ""} onChange={(e) => setItem(i, "valid_from", e.target.value)} className="h-8 text-xs w-[140px] shrink-0" title="Inizio" />
              <Button type="button" size="sm" variant="outline" className="h-8 shrink-0" onClick={() => saveEdit(it)} data-testid={`attendance-save-${it.id}`}>Salva</Button>
              <button type="button" className="p-1.5 text-red-500 hover:bg-red-50 rounded-md shrink-0" title="Rimuovi dal contesto" onClick={() => endOne(it)} data-testid={`attendance-remove-${it.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}

      <div className="border-t border-slate-200 pt-2">
        <div className="flex items-center gap-2 mb-2">
          <Label className="text-xs text-slate-500">Inizio associazione</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 text-xs w-[150px]" data-testid="attendance-from" />
        </div>
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2 mb-1.5">
            <Input value={r.nome} onChange={(e) => setRow(i, "nome", e.target.value)} onPaste={(e) => handlePaste(e, i, 0)} className="h-8 text-sm flex-1 min-w-0" placeholder="Nome" data-testid={`attendance-new-nome-${i}`} />
            <Input value={r.cognome} onChange={(e) => setRow(i, "cognome", e.target.value)} onPaste={(e) => handlePaste(e, i, 1)} className="h-8 text-sm flex-1 min-w-0" placeholder="Cognome" data-testid={`attendance-new-cognome-${i}`} />
            <Input value={r.telefono} onChange={(e) => setRow(i, "telefono", e.target.value)} onPaste={(e) => handlePaste(e, i, 2)} className="h-8 text-sm flex-1 min-w-0" placeholder="Telefono" data-testid={`attendance-new-telefono-${i}`} />
            {rows.length > 1 && <button type="button" className="p-1.5 text-slate-400 hover:text-red-500 shrink-0" onClick={() => delRow(i)}><X className="h-4 w-4" /></button>}
          </div>
        ))}
        <div className="flex flex-wrap items-center gap-2 mt-1">
          <Button type="button" size="sm" variant="outline" className="h-8" onClick={addRow} data-testid="attendance-add-row"><UserPlus className="h-3.5 w-3.5 mr-1" /> Riga</Button>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-500">Numero righe</span>
            <Input type="number" min={1} max={500} value={addN} onChange={(e) => setAddN(e.target.value)}
              className="h-8 w-16 text-sm" data-testid="attendance-add-n-input" />
            <Button type="button" size="sm" variant="outline" className="h-8" onClick={() => addRows(addN)} data-testid="attendance-add-n-btn">Aggiungi righe</Button>
          </div>
          <Button type="button" size="sm" className="h-8 bg-[#22364D] hover:bg-[#2f4a6b]" onClick={saveRows} data-testid="attendance-save-new">Aggiungi nominativi</Button>
        </div>
        <p className="text-[11px] text-slate-400 mt-1.5">Suggerimento: incolla più righe da Excel (Nome, Cognome, Telefono) direttamente nei campi qui sopra.</p>
      </div>
    </div>
  );
}

export default AttendanceParticipants;
